import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store/types/sharedTypes';
import {
  Accordion,
  Button,
  Group,
  Icon,
  Banner,
  Stack,
  H7,
  Body,
} from '@qred/components-library';
import { onChangeInputEvent, onFocusSelectEvent } from '~/types/types';
import { pushToGtmOnboardingAction } from '~/store/actions/gtmActions';
import { ValidationContext } from '~/components/hoc/withValidation';
import { validateFetchedBeneficialOwner } from '~/helpers/onboarding.helper';
import useModals from '~/hooks/useModals';
import useOnboardingMarketConfig from '~/hooks/useOnboardingMarketConfig';
import { useOnboardingOfferSelectors } from '~/store/selectors/onboardingOffer.selector';
import BeneficialOwner from './BeneficialOwner';
import OwnerManagementModal from '../OwnershipAdditionalOwnersPanel/OwnerManagementModal';
import OwnershipUploadDocumentationPanel from '../OwnershipUploadDocumentationPanel/OwnershipUploadDocumentationPanel';

const FetchedBeneficialOwners = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();

  const validationContext = useContext(ValidationContext);
  const {
    onboardingOffer: { form },
    matchedMedia: { mobile },
  } = useSelector((state: RootState) => state);

  const {
    marketHasFetchedOwnershipSharesAsRanges,
    marketRequiresPersonalNumberForFetchedOwners,
    marketRequiresDateOfBirthForFetchedOwners,
    marketRequiresYearOfBirthForFetchedOwners,
  } = useOnboardingMarketConfig();

  const {
    fetchedOwnersHaveBeenChanged,
    totalAmountOfShares,
  } = useOnboardingOfferSelectors();

  const findFirstInvalidAccordionItem = () => {
    const invalidBeneficialOwner = form.owners.find(
      (beneficialOwner) => !validateFetchedBeneficialOwner(beneficialOwner)
    );
    if (invalidBeneficialOwner) {
      return invalidBeneficialOwner.id.toString();
    }

    return null;
  };
  const [accordionValue, setAccordionValue] = useState<string | null>(
    findFirstInvalidAccordionItem()
  );

  const onBlur = (event: onChangeInputEvent | onFocusSelectEvent | string) => {
    let name;
    if (typeof event === 'string') {
      name = event;
    } else {
      name = event.target.name;
    }
    dispatch(
      pushToGtmOnboardingAction({
        actionName: `${name}_change`,
      })
    );
  };

  useEffect(() => {
    const fetchedBeneficialOwnersHasErrors = form.owners.some(
      (owner) => !validateFetchedBeneficialOwner(owner)
    );

    if (fetchedBeneficialOwnersHasErrors) {
      validationContext.addPropertyToValidationErrors(
        'fetchedBeneficialOwners'
      );
    }

    const firstInvalidAccordionItem = findFirstInvalidAccordionItem();
    setAccordionValue(firstInvalidAccordionItem);

    return () => {
      validationContext.removePropertyFromValidationErrors(
        'fetchedBeneficialOwners'
      );
    };
  }, [form.owners, totalAmountOfShares]);

  const modals = useModals();

  const openOwnerManagementModal = () => {
    dispatch(
      pushToGtmOnboardingAction({
        actionName: 'loan_offer_add_owner',
      })
    );

    modals.openModal({
      header: <H7>{translate('OnboardingOffer.AddNewOwner')}</H7>,
      children: (
        <OwnerManagementModal
          ownershipPercentageAsRanges={marketHasFetchedOwnershipSharesAsRanges}
          askForPersonalNumber={marketRequiresPersonalNumberForFetchedOwners}
          askForDateOfBirth={marketRequiresDateOfBirthForFetchedOwners}
          askForYearOfBirth={marketRequiresYearOfBirthForFetchedOwners}
        />
      ),
      size: 'md',
      fullScreen: mobile,
      filledBackground: true,
      hasOwnFooter: true,
    });
  };

  const applicantCanAddAdditionalBeneficialOwners = totalAmountOfShares < 75;

  return (
    <>
      <Body size="lg">
        {translate('OnboardingOffer.OwnershipFetchedOwnersDescription')}
      </Body>
      <Accordion value={accordionValue} onChange={setAccordionValue}>
        {!!form.owners?.length &&
          form.owners.map((beneficialOwner) => (
            <Accordion.Item
              value={beneficialOwner.id.toString()}
              key={beneficialOwner.id}
            >
              <Accordion.Control>
                <Group align="center">
                  <Icon
                    src={
                      validateFetchedBeneficialOwner(beneficialOwner)
                        ? 'CheckCircle'
                        : 'CheckCircleUnfilled'
                    }
                    size="lg"
                    iconSize={16}
                  />
                  {beneficialOwner.fullName}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <BeneficialOwner
                  key={beneficialOwner.id}
                  beneficialOwner={beneficialOwner}
                  onBlur={onBlur}
                />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
      </Accordion>

      {applicantCanAddAdditionalBeneficialOwners && (
        <Stack align="center">
          <Body size="lg">
            {translate('OnboardingOffer.FetchedBeneficialOwnersAddOwnerLabel')}
          </Body>
          <Button
            onClick={openOwnerManagementModal}
            variant="secondary"
            fullWidth={mobile}
          >
            <Icon src="Plus" mr="0.5em" />
            {translate(
              'OnboardingOffer.AdditionalOwnersPanelAddOwnerButtonLabel'
            )}
          </Button>
        </Stack>
      )}

      <Banner
        state="critical"
        withCloseButton={false}
        message={translate('OnboardingOffer.TotalOwnershipSharesMoreThan100')}
        opened={
          validationContext.validationErrors.totalOwnershipSharesMoreThan100
        }
      />

      {fetchedOwnersHaveBeenChanged && <OwnershipUploadDocumentationPanel />}
    </>
  );
};

export default FetchedBeneficialOwners;
